// Products.js - Adam International Academy Academic Programs & Solutions
import React, { useState, useEffect, useRef } from 'react';
import './Products.css';
import { Link } from 'react-router-dom';

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations - DÜZELTİLDİ
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' } // threshold düşürüldü, rootMargin eklendi
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // TÜM SECTION'LARI BAŞLANGIÇTA GÖRÜNÜR YAP (kullanıcı deneyimi için)
  useEffect(() => {
    // Sayfa yüklendiğinde tüm section'ları görünür yap
    const allSections = [
      'features', 'sectors', 'categories', 'filter', 
      'products', 'consulting', 'testimonials', 'cta'
    ];
    
    const initialVisible = {};
    allSections.forEach(section => {
      initialVisible[section] = true;
    });
    
    setVisibleSections(initialVisible);
  }, []);

  // Academic Program Categories from documents
  const categories = [
    { id: 'all', name: 'All Programs', icon: '📚', count: 16 },
    { id: 'elearning', name: 'e-Learning Courses', icon: '💻', count: 5 },
    { id: 'certificates', name: 'Professional Certificates', icon: '🏅', count: 4 },
    { id: 'diplomas', name: 'Professional Diplomas', icon: '📜', count: 3 },
    { id: 'degrees', name: 'Degree Programs', icon: '🎓', count: 2 },
    { id: 'workshops', name: 'Workshops & Training', icon: '👥', count: 2 }
  ];

  // Academic Programs from Adam Academy documents
  const products = [
    // e-Learning Courses
    {
      id: 1,
      title: 'Sustainable Development Goals (SDG 2030) Implementation',
      description: 'Comprehensive online course on understanding and implementing the 17 Sustainable Development Goals in organizational strategy and operations.',
      category: 'elearning',
      features: [
        'Self-paced learning with 24/7 access',
        'SDG 4 (Quality Education) focus',
        'Multilingual content (English, Arabic, Turkish)',
        'Interactive assessments and case studies',
        'Certificate of completion',
        'Peer-reviewed by international experts'
      ],
      icon: '🌍',
      price: '$299',
      popular: true,
      new: false,
      rating: 4.9,
      duration: '10 weeks',
      instructor: 'Prof. Dr. Ayşe Demir'
    },
    {
      id: 2,
      title: 'Multilingual Education & Capacity Development',
      description: 'Learn strategies for designing and delivering effective educational programs in multiple languages, with focus on culturally sensitive approaches.',
      category: 'elearning',
      features: [
        'Multilingual content adaptation',
        'Culturally sensitive design',
        'Assessment in multiple languages',
        'Case studies from Africa & Middle East',
        'Expert-led instruction',
        'Practical assignments'
      ],
      icon: '🌐',
      price: '$279',
      popular: true,
      new: false,
      rating: 4.8,
      duration: '8 weeks',
      instructor: 'Prof. Dr. Samir Albuny'
    },
    {
      id: 3,
      title: 'Artificial Intelligence for Sustainable Development',
      description: 'Explore how AI technologies can be leveraged to address development challenges and achieve SDG targets.',
      category: 'elearning',
      features: [
        'AI fundamentals for development',
        'Machine learning applications',
        'Natural language processing',
        'Ethical AI considerations',
        'Case studies in healthcare and education',
        'Hands-on projects'
      ],
      icon: '🤖',
      price: '$329',
      popular: true,
      new: true,
      rating: 4.7,
      duration: '10 weeks',
      instructor: 'Prof. Dr. Kevin Zhang'
    },
    {
      id: 4,
      title: 'Project Management for Development Professionals',
      description: 'Comprehensive project management training tailored for international development projects and SDG-aligned initiatives.',
      category: 'elearning',
      features: [
        'Project lifecycle management',
        'Logical framework approach',
        'Stakeholder analysis',
        'Risk management',
        'Monitoring & evaluation',
        'Budgeting & financial management'
      ],
      icon: '📋',
      price: '$269',
      popular: false,
      new: false,
      rating: 4.8,
      duration: '8 weeks',
      instructor: 'Prof. Dr. Nabil Bukairy'
    },
    {
      id: 5,
      title: 'Cybersecurity & Data Protection',
      description: 'Essential cybersecurity skills for protecting organizational data and infrastructure in the digital age.',
      category: 'elearning',
      features: [
        'Cybersecurity fundamentals',
        'Network security',
        'Data encryption',
        'Security risk assessment',
        'Incident response',
        'Compliance and regulations'
      ],
      icon: '🔒',
      price: '$299',
      popular: false,
      new: true,
      rating: 4.8,
      duration: '8 weeks',
      instructor: 'Dr. Susan Chen'
    },

    // Professional Certificates
    {
      id: 6,
      title: 'Certificate in International Relations & Diplomacy',
      description: 'Professional certificate program covering international relations theories, diplomatic protocols, and multilateral negotiations.',
      category: 'certificates',
      features: [
        'Theories of international relations',
        'Diplomatic protocol',
        'Multilateral negotiations',
        'International law fundamentals',
        'Crisis diplomacy',
        'UN system and international organizations'
      ],
      icon: '🌐',
      price: '$649',
      popular: true,
      new: false,
      rating: 4.9,
      duration: '12 weeks',
      instructor: 'Prof. Dr. Hamman Adam'
    },
    {
      id: 7,
      title: 'Certificate in Renewable Energy & Sustainability',
      description: 'Comprehensive overview of renewable energy technologies and their role in sustainable development.',
      category: 'certificates',
      features: [
        'Solar energy systems',
        'Wind power technology',
        'Energy storage solutions',
        'Grid integration',
        'Energy policy and economics',
        'Sustainable energy projects'
      ],
      icon: '☀️',
      price: '$599',
      popular: true,
      new: false,
      rating: 4.7,
      duration: '10 weeks',
      instructor: 'Prof. Dr. Maria Garcia'
    },
    {
      id: 8,
      title: 'Certificate in Women Empowerment & Leadership (LEADWomen)',
      description: 'Specialized program designed to develop women leaders and promote gender equality in organizations.',
      category: 'certificates',
      features: [
        'Women in leadership',
        'Overcoming barriers',
        'Building confidence and presence',
        'Networking and mentorship',
        'Work-life integration',
        'Creating inclusive organizations'
      ],
      icon: '👩‍💼',
      price: '$549',
      popular: true,
      new: false,
      rating: 4.9,
      duration: '8 weeks',
      instructor: 'Prof. Dr. Nancy Adams'
    },
    {
      id: 9,
      title: 'Certificate in HSE Management',
      description: 'Comprehensive training on occupational health, safety standards, and environmental management systems.',
      category: 'certificates',
      features: [
        'HSE management systems',
        'Risk assessment',
        'Occupational health standards',
        'Environmental compliance',
        'Emergency response planning',
        'Incident investigation'
      ],
      icon: '⚠️',
      price: '$529',
      popular: false,
      new: false,
      rating: 4.7,
      duration: '6 weeks',
      instructor: 'Dr. Emily Johnson'
    },

    // Professional Diplomas
    {
      id: 10,
      title: 'Diploma in International Law & Commercial Arbitration',
      description: 'Advanced diploma program on international legal frameworks and arbitration procedures for commercial disputes.',
      category: 'diplomas',
      features: [
        'Sources of international law',
        'International commercial contracts',
        'Arbitration agreements',
        'Arbitration procedures',
        'Award enforcement',
        'Investment arbitration'
      ],
      icon: '⚖️',
      price: '$1,299',
      popular: true,
      new: false,
      rating: 4.9,
      duration: '12 weeks',
      instructor: 'Prof. Dr. Najib Al-Rahman'
    },
    {
      id: 11,
      title: 'Diploma in Economic Development & Policy Analysis',
      description: 'Advanced course on economic development theories, policy formulation, and impact assessment.',
      category: 'diplomas',
      features: [
        'Development economics',
        'Policy formulation',
        'Economic feasibility studies',
        'Impact assessment',
        'Poverty reduction strategies',
        'Sustainable economic growth'
      ],
      icon: '📊',
      price: '$1,199',
      popular: true,
      new: false,
      rating: 4.8,
      duration: '12 weeks',
      instructor: 'Prof. Dr. Abdulnasir Munir'
    },
    {
      id: 12,
      title: 'Diploma in Digital Transformation & Innovation',
      description: 'Strategies for leading digital transformation initiatives and fostering innovation in organizations.',
      category: 'diplomas',
      features: [
        'Digital strategy development',
        'Technology adoption',
        'Innovation management',
        'Change leadership',
        'Digital culture',
        'Emerging technologies'
      ],
      icon: '🔄',
      price: '$1,099',
      popular: false,
      new: true,
      rating: 4.8,
      duration: '8 weeks',
      instructor: 'Prof. Dr. Michael Brown'
    },

    // Degree Programs
    {
      id: 13,
      title: 'Master of Arts in International Relations',
      description: 'Comprehensive graduate program focusing on global politics, diplomacy, and international cooperation.',
      category: 'degrees',
      features: [
        'International relations theory',
        'Global governance',
        'International security',
        'Foreign policy analysis',
        'Research methods',
        'Thesis or capstone project'
      ],
      icon: '🎓',
      price: '$8,500',
      popular: true,
      new: false,
      rating: 4.9,
      duration: '18 months',
      instructor: 'Prof. Dr. Hamman Adam'
    },
    {
      id: 14,
      title: 'Master of Science in Sustainable Development',
      description: 'Interdisciplinary graduate program addressing global challenges through sustainable development frameworks.',
      category: 'degrees',
      features: [
        'SDG framework and implementation',
        'Environmental sustainability',
        'Social and economic development',
        'Policy analysis',
        'Research methodology',
        'Field research project'
      ],
      icon: '🌱',
      price: '$8,500',
      popular: true,
      new: false,
      rating: 4.9,
      duration: '18 months',
      instructor: 'Prof. Dr. Ayşe Demir'
    },

    // Workshops & Training
    {
      id: 15,
      title: 'Executive Leadership Workshop',
      description: 'Intensive workshop for senior executives on strategic leadership, decision-making, and organizational transformation.',
      category: 'workshops',
      features: [
        'Strategic thinking',
        'Decision-making frameworks',
        'Change management',
        'Team leadership',
        'Crisis management',
        'Personal leadership development'
      ],
      icon: '👑',
      price: '$1,499',
      popular: true,
      new: false,
      rating: 4.9,
      duration: '3 days',
      instructor: 'Dr. M. Haydar'
    },
    {
      id: 16,
      title: 'Academic Writing & Research Methodology Workshop',
      description: 'Hands-on workshop for researchers and graduate students on academic writing, publication strategies, and research methods.',
      category: 'workshops',
      features: [
        'Research design',
        'Literature review',
        'Academic writing',
        'Publication strategies',
        'Peer review process',
        'Ethical considerations'
      ],
      icon: '✍️',
      price: '$699',
      popular: false,
      new: true,
      rating: 4.7,
      duration: '2 days',
      instructor: 'Prof. Dr. Sarah Wilson'
    }
  ];

  // Academic features from documents
  const features = [
    {
      title: 'SDG 2030 Aligned Curriculum',
      description: 'All programs support the Sustainable Development Goals, with special focus on SDG 4: Quality Education.',
      icon: '🌍'
    },
    {
      title: 'Multilingual Education',
      description: 'Courses available in English, Arabic, and Turkish for global accessibility and cultural sensitivity.',
      icon: '🌐'
    },
    {
      title: 'Expert Faculty',
      description: 'Learn from 100+ international experts and practitioners from 30+ countries.',
      icon: '👥'
    },
    {
      title: 'Flexible Learning',
      description: 'Choose from self-paced eLearning, blended learning, or intensive workshops to suit your schedule.',
      icon: '⏱️'
    }
  ];

  // Adam 9 Sectors from documents
  const adamSectors = [
    'Commerce', 'Education', 'Consultancy', 'Industry', 
    'Economy', 'Construction', 'Technology', 'Services', 'Sustainable Development'
  ];

  // Filter and sort products
  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(product => product.category === activeCategory);
  
  const searchedProducts = filteredProducts.filter(product => 
    product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.instructor && product.instructor.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const sortedProducts = [...searchedProducts].sort((a, b) => {
    if (sortBy === 'rating') {
      return b.rating - a.rating;
    } else if (sortBy === 'name') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'popular') {
      return (b.popular ? 1 : 0) - (a.popular ? 1 : 0);
    } else if (sortBy === 'price') {
      const priceA = parseFloat(a.price.replace(/[^0-9.-]+/g, ''));
      const priceB = parseFloat(b.price.replace(/[^0-9.-]+/g, ''));
      return priceA - priceB;
    }
    return a.id - b.id;
  });

  return (
    <div className="products-container">
      {/* Hero Section - Updated */}
      <section className="ap-hero">
        <div className="ap-hero-overlay">
          <div className="ap-hero-content">
            <h1>Adam International Academy Programs</h1>
            <p>SDG-aligned eLearning courses, professional certificates, diplomas, and degree programs for global professionals</p>
            <div className="hero-search">
              <input 
                type="text" 
                placeholder="Search programs by title, description, or instructor..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search programs"
              />
              <button aria-label="Search">Search</button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Updated */}
      <section 
        className="features-section"
        ref={el => sectionRefs.current.features = el}
        data-section="features"
      >
        <div className={`container ${visibleSections.features ? 'visible' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">Why Adam Academy?</h2>
            <p className="section-subtitle">Excellence in capacity development and global education</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section 
        className="sectors-section"
        ref={el => sectionRefs.current.sectors = el}
        data-section="sectors"
      >
        <div className={`container ${visibleSections.sectors ? 'visible' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">Adam 9 Sectors</h2>
            <p className="section-subtitle">Our programs span all nine sectors of Adam Holding Union</p>
          </div>
          <div className="sectors-grid">
            {adamSectors.map((sector, index) => (
              <div key={index} className="sector-badge">
                <span>{sector}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Categories - Updated */}
      <section 
        className="categories-section"
        ref={el => sectionRefs.current.categories = el}
        data-section="categories"
      >
        <div className={`container ${visibleSections.categories ? 'visible' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">Explore by Program Type</h2>
            <p className="section-subtitle">Find the perfect program for your professional development goals</p>
          </div>
          <div className="categories-grid">
            {categories.map(category => (
              <div 
                key={category.id}
                className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && setActiveCategory(category.id)}
              >
                <div className="category-icon">{category.icon}</div>
                <div className="category-content">
                  <h3 className="category-title">{category.name}</h3>
                  <span className="category-count">{category.count} programs</span>
                </div>
                <div className="category-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Filter and Sort */}
      <section 
        className="filter-section"
        ref={el => sectionRefs.current.filter = el}
        data-section="filter"
      >
        <div className={`container ${visibleSections.filter ? 'visible' : ''}`}>
          <div className="filter-controls">
            <div className="search-box">
              <input 
                type="text" 
                placeholder="Search programs by name, description, or instructor..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search programs"
              />
            </div>
            <div className="sort-box">
              <label htmlFor="sort-select">Sort by:</label>
              <select 
                id="sort-select"
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort programs"
              >
                <option value="default">Default</option>
                <option value="name">Name</option>
                <option value="rating">Rating</option>
                <option value="popular">Popular</option>
                <option value="price">Price</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section 
        className="products-section"
        ref={el => sectionRefs.current.products = el}
        data-section="products"
      >
        <div className={`container ${visibleSections.products ? 'visible' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">
              {activeCategory === 'all' ? 'All Programs' : `${categories.find(c => c.id === activeCategory)?.name}`}
              <span className="product-count"> ({sortedProducts.length} programs)</span>
            </h2>
            <p className="section-subtitle">
              {activeCategory === 'all' 
                ? 'Comprehensive capacity development programs' 
                : `Specialized ${categories.find(c => c.id === activeCategory)?.name.toLowerCase()} for professionals`
              }
            </p>
          </div>
          
          {sortedProducts.length === 0 ? (
            <div className="no-products">
              <h3>No programs found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="products-grid">
              {sortedProducts.map(product => (
                <div 
                  key={product.id} 
                  className={`product-card ${hoveredProduct === product.id ? 'hovered' : ''}`}
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                >
                  <div className="product-badges">
                    {product.popular && <span className="badge popular">Popular</span>}
                    {product.new && <span className="badge new">New</span>}
                  </div>
                  
                  <div className="product-icon">{product.icon}</div>
                  
                  <div className="product-content">
                    <h3 className="product-title">{product.title}</h3>
                    <div className="product-rating">
                      {'★'.repeat(Math.floor(product.rating))}
                      {'☆'.repeat(5 - Math.floor(product.rating))}
                      <span>({product.rating})</span>
                    </div>
                    <p className="product-desc">{product.description.substring(0, 120)}...</p>
                    
                    <div className="product-meta">
                      <span className="product-duration">⏱️ {product.duration}</span>
                      {product.instructor && <span className="product-instructor">👨‍🏫 {product.instructor}</span>}
                    </div>
                    
                    <ul className="product-features">
                      {product.features.slice(0, 3).map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                      {product.features.length > 3 && (
                        <li className="more-features">+{product.features.length - 3} more features</li>
                      )}
                    </ul>
                    
                    <div className="product-price">{product.price}</div>
                  </div>
                  
                  <div className="product-actions">
                    <button className="btn primary">Enroll Now</button>
                    <button className="btn secondary">View Details</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Learning Formats Section */}
      <section 
        className="consulting-section"
        ref={el => sectionRefs.current.consulting = el}
        data-section="consulting"
      >
        <div className={`container ${visibleSections.consulting ? 'visible' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">📚 Learning Formats</h2>
            <p className="section-subtitle">Choose the format that works best for your schedule and learning style</p>
          </div>
          <div className="consulting-grid">
            <div className="consulting-item">
              <div className="consulting-icon">⏱️</div>
              <p>Self-Paced eLearning</p>
            </div>
            <div className="consulting-item">
              <div className="consulting-icon">🔄</div>
              <p>Blended Learning</p>
            </div>
            <div className="consulting-item">
              <div className="consulting-icon">🌍</div>
              <p>MOOCs</p>
            </div>
            <div className="consulting-item">
              <div className="consulting-icon">🎥</div>
              <p>Technical Webinars</p>
            </div>
            <div className="consulting-item">
              <div className="consulting-icon">📱</div>
              <p>Mobile Learning</p>
            </div>
            <div className="consulting-item">
              <div className="consulting-icon">👨‍🏫</div>
              <p>Online Tutored Courses</p>
            </div>
            <div className="consulting-item">
              <div className="consulting-icon">🏛️</div>
              <p>Face-to-Face Workshops</p>
            </div>
            <div className="consulting-item">
              <div className="consulting-icon">🎓</div>
              <p>University Degree Programs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Updated */}
      <section 
        className="testimonials-section"
        ref={el => sectionRefs.current.testimonials = el}
        data-section="testimonials"
      >
        <div className={`container ${visibleSections.testimonials ? 'visible' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">What Our Students Say</h2>
            <p className="section-subtitle">Trusted by professionals from 30+ countries</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"The SDG Implementation course gave me practical tools to integrate sustainability into our organization's strategy. The multilingual approach was invaluable for our international team."</p>
              </div>
              <div className="testimonial-author">
                <h4>Ahmed Hassan</h4>
                <p>Sustainability Director, Cairo</p>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"As a policy maker, the International Relations course provided exactly what I needed for my work with multilateral organizations. The faculty expertise is outstanding."</p>
              </div>
              <div className="testimonial-author">
                <h4>Fatima Al-Sayed</h4>
                <p>Policy Advisor, UAE</p>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"The flexible learning format allowed me to complete my certification while working full-time. The mobile learning app made it possible to study anywhere."</p>
              </div>
              <div className="testimonial-author">
                <h4>Mehmet Yılmaz</h4>
                <p>Project Manager, Istanbul</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Updated */}
      <section 
        className="cta-section"
        ref={el => sectionRefs.current.cta = el}
        data-section="cta"
      >
        <div className={`container ${visibleSections.cta ? 'visible' : ''}`}>
          <div className="cta-content">
            <h2 className="cta-title">Ready to Advance Your Career?</h2>
            <p className="cta-desc">
              Join our global community of professionals and leaders committed to sustainable development
            </p>
            <div className="cta-buttons">
              <Link to="/apply" className="btn primary large">Apply Now</Link>
              <Link to="/contact" className="btn outline large">Contact Admissions</Link>
            </div>
            <p className="cta-locations">📍 Campuses in Istanbul, Turkey | Manama, Bahrain</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;